import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import GenericState from '../../MyComponents/GenericLoadingErrorState/GenericState';
import DeleteIcon from '../../MyComponents/icons/DeleteIcon';
import MessageBody, { messageTitle } from './MessageBody';
import userApi from '../../Api/userApi';
import store from '../../redux/store';
import { updateUserMessages } from '../../redux/actions';
import { CARD, PILL_SECONDARY, TEXT_LINK, cx } from '../Home/HomeDashboard/dashboardStyles';
import { COLORS2, REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

// Below this the date folds under the title.
const NARROW = '@media (max-width: 700px)';

const useStyles = makeStyles(() => ({
    intro: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '16px 24px',
        marginBottom: '26px',
    },
    eyebrowRow: {
        marginBottom: '16px',
    },
    title: {
        margin: '0 0 10px',
        fontSize: 'clamp(1.9rem, 3.6vw, 2.8rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    lead: {
        maxWidth: '620px',
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '1.04rem',
        lineHeight: 1.6,
    },
    unread: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 14px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.link,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        whiteSpace: 'nowrap',
    },
    list: {
        ...CARD,
        margin: 0,
        padding: 0,
        listStyle: 'none',
        overflow: 'hidden',
    },
    row: {
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        '&:last-child': {
            borderBottom: 0,
        },
    },
    rowUnread: {
        background: REDESIGN_COLORS.cream,
    },
    // The whole summary line is the toggle.
    summary: {
        display: 'grid',
        gridTemplateColumns: '14px minmax(0, 1fr) auto auto 20px',
        alignItems: 'center',
        gap: '14px',
        width: '100%',
        padding: '16px 22px',
        border: 0,
        background: 'none',
        fontFamily: REDESIGN_FONTS.body,
        textAlign: 'left',
        cursor: 'pointer',
        [NARROW]: {
            gridTemplateColumns: '14px minmax(0, 1fr) 20px',
            gap: '12px',
            padding: '14px 16px',
        },
    },
    dot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: 'transparent',
        border: `2px solid ${REDESIGN_COLORS.line}`,
    },
    dotUnread: {
        background: REDESIGN_COLORS.primary,
        borderColor: REDESIGN_COLORS.primary,
    },
    main: {
        minWidth: 0,
    },
    rowTitle: {
        margin: 0,
        fontSize: '1rem',
        lineHeight: 1.35,
        fontWeight: REDESIGN_FONTS.weightStrong,
        color: REDESIGN_COLORS.dark,
        overflowWrap: 'anywhere',
    },
    rowTitleUnread: {
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    // On phones the date reads under the title.
    meta: {
        display: 'none',
        margin: '3px 0 0',
        color: REDESIGN_COLORS.body,
        fontSize: '0.84rem',
        [NARROW]: {
            display: 'block',
        },
    },
    time: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.link,
        fontSize: '0.8rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        whiteSpace: 'nowrap',
        [NARROW]: {
            display: 'none',
        },
    },
    date: {
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
        [NARROW]: {
            display: 'none',
        },
    },
    chevron: {
        color: REDESIGN_COLORS.muted,
        transition: 'transform 150ms ease',
    },
    chevronOpen: {
        transform: 'rotate(180deg)',
    },
    body: {
        padding: '0 22px 20px 50px',
        [NARROW]: {
            padding: '0 16px 18px 42px',
        },
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px 18px',
        marginTop: '16px',
    },
    markUnread: {
        ...TEXT_LINK,
        padding: 0,
        border: 0,
        background: 'none',
        fontFamily: REDESIGN_FONTS.body,
        cursor: 'pointer',
    },
    remove: {
        ...PILL_SECONDARY,
        minHeight: '38px',
        padding: '0 14px',
        fontSize: '0.86rem',
        borderColor: 'rgba(243, 83, 37, 0.4)',
        color: COLORS2.virtualRed,
        '&:hover': {
            borderColor: COLORS2.virtualRed,
            color: COLORS2.virtualRed,
        },
        '& svg': {
            width: '16px',
            height: '16px',
        },
    },
    empty: {
        ...CARD,
        padding: '26px 28px',
        background: REDESIGN_COLORS.mist,
        borderColor: REDESIGN_COLORS.primaryBorderSoft,
        color: REDESIGN_COLORS.body,
        fontSize: '0.98rem',
        lineHeight: 1.6,
    },
    state: {
        ...CARD,
        padding: '26px 28px',
    },
}));

const DATE_FORMAT = { year: 'numeric', month: 'long', day: 'numeric' };
const TIME_FORMAT = { hour: 'numeric', minute: 'numeric' };

/**
 * The account's messages: subscription, invoice and payment notices as a
 * list of rows that open in place. Opening a message marks it read; it can
 * be marked unread again or deleted.
 */
const UserMessages = ({ userMessages }) => {
    const classes = useStyles();
    const intl = useIntl();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        userApi
            .getUserMessages()
            .then((res) => {
                store.dispatch(updateUserMessages(res.data));
                setIsLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setIsError(true);
            });
    }, []);

    const remove = (id) => {
        setIsLoading(true);
        userApi
            .deleteUserMessage(id)
            .then((res) => {
                store.dispatch(updateUserMessages(res.data));
                setIsLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setIsError(true);
            });
    };

    const markRead = (id, value) => {
        userApi
            .markReadUserMessage(id, value)
            .then((res) => store.dispatch(updateUserMessages(res.data)))
            .catch((err) => {
                console.error(err);
                setIsError(true);
            });
    };

    const toggle = (id) => {
        const next = expanded === id ? null : id;
        setExpanded(next);
        if (next) markRead(next, true);
    };

    const makeUnread = (id) => {
        setExpanded(null);
        markRead(id, false);
    };

    const data = (userMessages && userMessages.userMessages) || [];
    const unreadCount = data.filter((row) => !row.isRead).length;

    return (
        <Grid item xs={12}>
            <LandingSection id="top" background="none" gutter={0} paddingTop={8} paddingBottom={56}>
                <div className={classes.intro}>
                    <div>
                        <div className={classes.eyebrowRow}>
                            <SectionEyebrow>
                                <FormattedMessage id="messages" />
                            </SectionEyebrow>
                        </div>
                        <h1 className={classes.title}>
                            <FormattedMessage id="messages.title" />
                        </h1>
                        <p className={classes.lead}>
                            <FormattedMessage id="redesign.messages.lead" />
                        </p>
                    </div>
                    {!isLoading && !isError && data.length > 0 ? (
                        <span className={classes.unread}>
                            <FormattedMessage id="redesign.messages.unread" values={{ count: unreadCount }} />
                        </span>
                    ) : null}
                </div>

                {isLoading || isError ? (
                    <div className={classes.state}>
                        <GenericState isLoading={isLoading} isError={isError} inline={true} />
                    </div>
                ) : data.length === 0 ? (
                    <div className={classes.empty}>
                        <FormattedMessage id="redesign.messages.empty" />
                    </div>
                ) : (
                    <ul className={classes.list}>
                        {data.map((row) => {
                            const open = expanded === row.id;
                            const when = row.createdAt ? new Date(row.createdAt) : null;
                            const date = when ? intl.formatDate(when, DATE_FORMAT) : '';
                            const time = when ? intl.formatTime(when, TIME_FORMAT) : '';
                            return (
                                <li key={row.id} className={cx(classes.row, !row.isRead && classes.rowUnread)}>
                                    <button
                                        type="button"
                                        aria-expanded={open}
                                        onClick={() => toggle(row.id)}
                                        className={classes.summary}
                                    >
                                        <span aria-hidden="true" className={cx(classes.dot, !row.isRead && classes.dotUnread)} />
                                        <span className={classes.main}>
                                            <p className={cx(classes.rowTitle, !row.isRead && classes.rowTitleUnread)}>
                                                {messageTitle(intl, row)}
                                            </p>
                                            {when ? (
                                                <p className={classes.meta}>
                                                    {date} · {time}
                                                </p>
                                            ) : null}
                                        </span>
                                        <span className={classes.time}>{time}</span>
                                        <span className={classes.date}>{date}</span>
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                            className={cx(classes.chevron, open && classes.chevronOpen)}
                                        >
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </button>
                                    {open ? (
                                        <div className={classes.body}>
                                            <MessageBody row={row} />
                                            <div className={classes.actions}>
                                                <button type="button" onClick={() => makeUnread(row.id)} className={classes.markUnread}>
                                                    <FormattedMessage id="messages.markUnread" />
                                                </button>
                                                <button type="button" onClick={() => remove(row.id)} className={classes.remove}>
                                                    <DeleteIcon />
                                                    <FormattedMessage id="messages.delete" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </LandingSection>
        </Grid>
    );
};

const mapStateToProps = (state) => {
    const { userMessages } = state;
    return { userMessages };
};

export default connect(mapStateToProps)(UserMessages);
